import * as React from "react";
import { format } from "date-fns";
// react-day-picker's own locale, not date-fns's: the same date-fns locale plus the
// calendar's aria labels (month and year lists, the arrows, each day) in that
// language. The bare date-fns `et` left them in English in an Estonian UI.
import { et as etLocale, enUS } from "react-day-picker/locale";
import { Calendar as CalendarIcon, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { Calendar } from "./calendar";
import { cn } from "../lib/utils";

interface DatePickerProps {
  /** ISO date string (YYYY-MM-DD) or empty. We use string here so it slots
   *  cleanly into form state next to other text inputs. */
  value?:    string;
  onChange:  (v: string) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Output locale ("en" | "et" | …) for the display label. */
  locale?:   string;
  className?: string;
  /** Allow clearing back to empty. Default true. */
  clearable?: boolean;
  /** Accessible name for the trigger. The button shows the chosen date and
   *  nothing else, so a filter row that names its fields only in a heading (or
   *  not at all) has to say which end of the range this one is. */
  ariaLabel?: string;
  /** Accessible name of the clear (x) control. Default English "clear". */
  clearLabel?: string;
  /** Min/max selectable dates. */
  fromDate?: Date;
  toDate?:   Date;
}

function parseISO(s?: string): Date | undefined {
  if (!s) return undefined;
  const d = new Date(s + "T00:00:00");
  return Number.isNaN(d.getTime()) ? undefined : d;
}

function toISO(d: Date): string {
  // Local-date YYYY-MM-DD - avoids timezone drift you get from toISOString()
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * Single-date picker - shadcn/Radix Popover triggering a react-day-picker
 * calendar. Replaces native <input type="date"> everywhere in the platform
 * so the look matches across browsers (chapter 0d.4).
 */
export function DatePicker({
  value, onChange, placeholder, disabled, locale, className, clearable = true, fromDate, toDate, ariaLabel,
  clearLabel = "clear",
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false);
  // Accept any Estonian locale tag ("et", "et-EE", …) - callers pass either.
  const isEt = (locale ?? "").toLowerCase().startsWith("et");
  const dfLocale = isEt ? etLocale : enUS;
  const date  = parseISO(value);
  const label = date ? format(date, isEt ? "dd.MM.yyyy" : "yyyy-MM-dd", { locale: dfLocale }) : "";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          // Named like the other primitives so a host app can retune the trigger
          // once in CSS instead of hand-passing utilities at each call site (the
          // CRM restyles it into its own field pill this way).
          data-slot="datepicker-trigger"
          aria-label={ariaLabel}
          disabled={disabled}
          className={cn(
            "h-10 w-full inline-flex items-center justify-between gap-2 rounded-md border border-input bg-card px-3 text-sm shadow-soft",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40",
            "disabled:cursor-not-allowed disabled:opacity-50 hover:bg-accent/30 transition-colors",
            !label && "text-muted-foreground",
            className,
          )}
        >
          <span className="inline-flex items-center gap-2 truncate">
            <CalendarIcon className="size-4 shrink-0 text-muted-foreground" />
            <span className="truncate tabular-nums">{label || placeholder || (isEt ? "Vali kuupäev" : "Pick a date")}</span>
          </span>
          {clearable && label && (
            <span
              role="button"
              tabIndex={-1}
              onClick={(e) => { e.stopPropagation(); onChange(""); }}
              // The visible disc stays 20px; the padding gives the thumb a 40px
              // target and the negative margin keeps the field the same height.
              className="-m-2.5 box-content inline-flex size-5 items-center justify-center rounded-sm p-2.5 text-muted-foreground hover:bg-destructive/15 hover:text-destructive"
              aria-label={clearLabel}
            >
              <X className="size-3" />
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0">
        <Calendar
          mode="single"
          locale={dfLocale}
          selected={date}
          // Open on the chosen date's month, not on today: a July date picked
          // in September used to open on September with the selection off
          // screen. `defaultMonth`, NOT `month` - the latter makes the shown
          // month controlled, and with no onMonthChange the prev/next arrows
          // would silently stop working. Radix mounts this subtree only while
          // the popover is open, so the default is re-read on every open.
          defaultMonth={date}
          onSelect={(d) => { if (d) { onChange(toISO(d)); setOpen(false); } }}
          startMonth={fromDate}
          endMonth={toDate}
          disabled={fromDate || toDate
            ? (d) => (fromDate ? d < fromDate : false) || (toDate ? d > toDate : false)
            : undefined}
          autoFocus
        />
      </PopoverContent>
    </Popover>
  );
}
